import React, { useEffect, useState, useCallback } from "react";
import "./RoleChild.css";
import Permission from "./Permission";
import {
    GetHierarchyRole,
    GetRolePermissionByRoleId,
    addChildRole,
} from "../../../services/roleService";

import CommonMessage from "../../../common/CommonMessage";
import CommonDropdown from "../../../common/CommonDropdown";
import { useNavigate } from "react-router-dom";
import GetPermission from "./GetPermission";

const RoleChild = () => {
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [name, setName] = useState("");
    const [status, setStatus] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // NEW
    const [selectedPermissions, setSelectedPermissions] = useState([]);

    // field-level errors
    const [errors, setErrors] = useState({
        selectedRole: "",
        name: "",
        status: "",
        permissions: "",
        form: "",
    });

    const [activeTab, setActiveTab] = useState("add");
    const [updateRoleId, setUpdateRoleId] = useState("");
    const [updateName, setUpdateName] = useState("");
    const [updateParentId, setUpdateParentId] = useState(0);
    const [updateStatus, setUpdateStatus] = useState("");
    const [updatePermissions, setUpdatePermissions] = useState([]);
    // safer recursive formatter
    const formatRoles = useCallback((rolesArray, parentPath = "") => {
        if (!Array.isArray(rolesArray)) return [];

        let result = [];

        for (const role of rolesArray) {
            const fullPath = parentPath
                ? `${parentPath} / ${role.roleName}`
                : role.roleName;

            result.push({
                id: role.id,
                name: fullPath,
            });

            if (role?.children?.length) {
                result = result.concat(formatRoles(role.children, fullPath));
            }
        }

        return result;
    }, []);

    // fetch roles
    useEffect(() => {
        const fetchRoles = async () => {
            setLoading(true);
            try {
                const response = await GetHierarchyRole();

                if (response?.statusCode === 200 && response?.success) {
                    setRoles(formatRoles(response.data));
                } else {
                    setErrors((prev) => ({
                        ...prev,
                        form: response?.message || "Failed to fetch roles",
                    }));
                }
            } catch (err) {
                setErrors((prev) => ({
                    ...prev,
                    form: err.message || "Something went wrong",
                }));
            } finally {
                setLoading(false);
            }
        };

        fetchRoles();
    }, [formatRoles]);

    useEffect(() => {
        const fetchRoleDetails = async () => {
            if (!updateRoleId) return;

            try {
                const response = await GetRolePermissionByRoleId(updateRoleId);

                if (response?.success) {
                    const role = response.data;
                    setUpdateParentId(role.parentId);
                    setUpdateName(role.roleName);
                    setUpdateStatus(
                        role.status ? "true" : "false"
                    );

                    setUpdatePermissions(
                        role.permissions.map(
                            (p) => p.permissionId
                        )
                    );
                }
            } catch (err) {
                console.error(err);
            }
        };

        fetchRoleDetails();
    }, [updateRoleId]);
    // validation
    const validate = () => {
        const newErrors = {
            selectedRole: "",
            name: "",
            status: "",
            permissions: "",
            form: "",
        };

        let isValid = true;

        if (!selectedRole) {
            newErrors.selectedRole = "Please select a role";
            isValid = false;
        }

        if (!name.trim()) {
            newErrors.name = "Please enter role name";
            isValid = false;
        } else if (name.trim().length < 2) {
            newErrors.name = "Role name must be at least 2 characters";
            isValid = false;
        }

        if (status === "") {
            newErrors.status = "Please select status";
            isValid = false;
        }
        // NEW
        if (selectedPermissions.length === 0) {
            newErrors.permissions =
                "Please select at least one permission";
            isValid = false;
        }
        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");

        if (!validate()) return;

        const payload = {
            parentId: Number(selectedRole),
            roleName: name.trim(),
            status: status === "true",
            permissions: selectedPermissions.map((id) => ({
                permissionId: id,
            })),
        };
        //console.log("FINAL PAYLOAD", payload);
        try {
            const response = await addChildRole(payload);
            if (response?.statusCode === 200 && response?.success) {
                setMessage(response.message || "Role added successfully");

                // reset form
                setSelectedRole("");
                setName("");
                setStatus("");
                setSelectedPermissions([]);
                setErrors({
                    selectedRole: "",
                    name: "",
                    status: "",
                    form: "",
                });
                navigate("/getrole");
            } else {
                setErrors((prev) => ({
                    ...prev,
                    form: response?.message || "Failed to add role",
                }));
            }
        } catch (err) {
            setErrors((prev) => ({
                ...prev,
                form: err.message || "Something went wrong",
            }));
        }
    };
    const handleUpdate = async (e) => {
        e.preventDefault();

        if (!updateRoleId) {
            setErrors((prev) => ({
                ...prev,
                form: "Please select a role",
            }));
            return;
        }

        if (!updateName.trim()) {
            setErrors((prev) => ({
                ...prev,
                form: "Role name is required",
            }));
            return;
        }

        if (updatePermissions.length === 0) {
            setErrors((prev) => ({
                ...prev,
                form: "Select at least one permission",
            }));
            return;
        }

        const payload = {
            id: Number(updateRoleId),
            parentId: Number(updateParentId),
            roleName: updateName.trim(),
            status: updateStatus === "true",
            permissions: updatePermissions.map((id) => ({
                permissionId: id,
            })),
        };

        try {
            const response = await addChildRole(payload);

            if (response?.success) {
                setMessage(
                    response.message ||
                    "Role updated successfully"
                );

                navigate("/getrole");
            } else {
                setErrors((prev) => ({
                    ...prev,
                    form:
                        response?.message ||
                        "Failed to update role",
                }));
            }
        } catch (err) {
            setErrors((prev) => ({
                ...prev,
                form:
                    err.message ||
                    "Something went wrong",
            }));
        }
    };
    return (
        <div className="container-fluid py-3">
            <div className="mb-3">
                <ul className="nav nav-tabs">
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === "add" ? "active" : ""
                                }`}
                            onClick={() => setActiveTab("add")}
                        >
                            Add Role
                        </button>
                    </li>

                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === "update" ? "active" : ""
                                }`}
                            onClick={() => setActiveTab("update")}
                        >
                            Update Role
                        </button>
                    </li>
                </ul>
            </div>
            {activeTab === "add" && (

                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-lg-6">
                            <div className="custom-card">
                                <div className="card-header-red">
                                    Admin Staff Details
                                </div>

                                <div className="card-body-custom">

                                    {/* Role Dropdown */}
                                    <div className="mb-3">
                                        <CommonDropdown
                                            label="Select Role type"
                                            options={roles}
                                            value={selectedRole}
                                            onChange={setSelectedRole}
                                            placeholder="Choose Role"
                                        />
                                        {errors.selectedRole && (
                                            <small className="text-danger">
                                                {errors.selectedRole}
                                            </small>
                                        )}
                                    </div>

                                    {/* Role Name */}
                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name ? "is-invalid" : ""
                                                }`}
                                            placeholder="Enter Role Name"
                                            value={name}
                                            onChange={(e) =>
                                                setName(e.target.value)
                                            }
                                        />
                                        {errors.name && (
                                            <small className="text-danger">
                                                {errors.name}
                                            </small>
                                        )}
                                    </div>

                                    {/* Status Dropdown */}
                                    <div className="mb-3">
                                        <select
                                            className={`form-select ${errors.status ? "is-invalid" : ""
                                                }`}
                                            value={status}
                                            onChange={(e) =>
                                                setStatus(e.target.value)
                                            }
                                        >
                                            <option value="">Select Status</option>
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>

                                        {errors.status && (
                                            <small className="text-danger">
                                                {errors.status}
                                            </small>
                                        )}
                                    </div>

                                    {/* Form Error */}
                                    {errors.form && (
                                        <div className="text-danger mb-3">
                                            {errors.form}
                                        </div>
                                    )}

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        disabled={loading}
                                    >
                                        {loading ? "Submitting..." : "Submit"}
                                    </button>
                                </div>
                            </div>
                            {/* Success Message */}
                            {message && <CommonMessage text={message} />}
                        </div>
                        {/* RIGHT */}
                        <div className="col-lg-6">
                            <div className="custom-card">
                                <div className="card-header-red">
                                    Permission
                                </div>

                                <div className="card-body-custom" style={{
                                    height: "250px",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                }}>
                                    <Permission
                                        selectedPermissions={
                                            selectedPermissions
                                        }
                                        setSelectedPermissions={
                                            setSelectedPermissions
                                        }
                                    />
                                    {errors.permissions && (
                                        <small className="text-danger">
                                            {errors.permissions}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            )}
            {activeTab === "update" && (
                <form onSubmit={handleUpdate}>
                    <div className="row">
                        {/* Left Side */}
                        <div className="col-lg-6">
                            <div className="custom-card">
                                <div className="card-header-red">
                                    Update Role
                                </div>

                                <div className="card-body-custom">
                                    <div className="mb-3">
                                        <CommonDropdown
                                            label="Select Role"
                                            options={roles.filter(role => role.id !== 1)}
                                            value={updateRoleId}
                                            onChange={setUpdateRoleId}
                                            placeholder="Choose Role"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={updateName}
                                            onChange={(e) =>
                                                setUpdateName(e.target.value)
                                            }
                                            placeholder="Role Name"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <select
                                            className="form-select"
                                            value={updateStatus}
                                            onChange={(e) =>
                                                setUpdateStatus(e.target.value)
                                            }
                                        >
                                            <option value="">
                                                Select Status
                                            </option>
                                            <option value="true">
                                                Active
                                            </option>
                                            <option value="false">
                                                Inactive
                                            </option>
                                        </select>
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                    >
                                        Update Role
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right Side */}
                        <div className="col-lg-6">
                            <div className="custom-card">
                                <div className="card-header-red">
                                    Permissions
                                </div>

                                <div
                                    className="card-body-custom"
                                    style={{
                                        height: "250px",
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                    }}
                                >
                                    <Permission
                                        selectedPermissions={updatePermissions}
                                        setSelectedPermissions={
                                            setUpdatePermissions
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            )}
            <div>
                <GetPermission
                    roleId={
                        activeTab === "add"
                            ? Number(selectedRole)
                            : Number(updateRoleId)
                    }
                />
            </div>

        </div>
    );
};

export default RoleChild;